import { Link, useOutletContext } from 'react-router-dom';
import { BookOpen, FolderOpen, Loader2, Pencil, Upload } from 'lucide-react';
import { DrawingUpload } from '../../components/DrawingUpload';
import { ProjectLibraryDocuments } from '../../components/project-detail/ProjectLibraryDocuments';
import { ProjectDetailSection } from '../../components/project-detail/ProjectDetailSection';
import { api } from '../../lib/api';
import { Tooltip } from '../../components/ui/Tooltip';
import { CUSTOMER_TYPE_OPTIONS } from '../../lib/customer-types';
import type { ProjectRecord } from '../../types/project';
import type { ProjectDetailOutletContext } from './outletTypes';

export function GegevensTab() {
  const v = useOutletContext<ProjectDetailOutletContext>();
  const draftProposal = v.proj.drawing_proposal;
  const proposalCount = draftProposal?.proposed_systems?.length ?? 0;

  const freshRevisionSeq = () => {
    const fresh = v.queryClient.getQueryData<ProjectRecord>(['project', v.projectId]);
    return fresh?.revision_seq ?? v.proj.revision_seq ?? 1;
  };

  return (
    <>
      <ProjectDetailSection
        id="project-context"
        icon={BookOpen}
        title="Dossiergegevens"
        description={
          v.contextExpanded ? (
            <>
              <strong className="text-text">Projecttype</strong> en <strong className="text-text">gebouwtype</strong>{' '}
              sturen het prijsboek (library). Bij een geldige <strong className="text-text">postcode</strong> wordt de{' '}
              <strong className="text-text">regio</strong> automatisch ingevuld (je kunt die nog handmatig aanpassen).
              Indexjaar wordt centraal toegepast.
            </>
          ) : (
            <>Zelfde gegevens als bij het aanmaken van het dossier. Alleen uitklappen als je iets wilt wijzigen.</>
          )
        }
        headerAction={
          <button
            type="button"
            onClick={() => v.setContextExpanded((e) => !e)}
            className={`${v.btnSecondary} text-sm`}
          >
            {v.contextExpanded ? (
              'Inklappen'
            ) : (
              <>
                <Pencil className="h-4 w-4" aria-hidden />
                Bewerken
              </>
            )}
          </button>
        }
      >
        {!v.contextExpanded ? (
          <div className="rounded-xl border border-border/70 bg-surface/90 px-4 py-4 text-sm">
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-text-muted">Projecttype</dt>
                <dd className="mt-0.5 font-medium text-text">{v.projectTypeLabel}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-text-muted">Gebouwtype</dt>
                <dd className="mt-0.5 font-medium text-text">{v.buildingTypeLabel}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-text-muted">Postcode</dt>
                <dd className="mt-0.5 font-mono font-medium text-text">{v.postalCode || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-text-muted">Regio (prijspeil)</dt>
                <dd className="mt-0.5 font-medium text-text">{v.regionDisplayLabel}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-text-muted">Klanttype</dt>
                <dd className="mt-0.5 font-medium text-text">{v.customerLabel}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-text-muted">
                  Prijspeil
                  <Tooltip
                    iconOnly
                    label="Uitleg prijspeil en indexjaar"
                    content="Het indexjaar (nu 2024) hoort bij het prijsindexmandje in de bibliotheek. In deze release staat daar alleen 2024 in; daarom is dat het vaste peil. Zo blijven berekeningen reproduceerbaar. Regio beïnvloedt de prijsfactor; indexjaar niet per project aanpasbaar."
                  />
                </dt>
                <dd className="mt-0.5 text-sm text-text">
                  Regio: {v.regionDisplayLabel} — index {v.indexForPricing}
                </dd>
              </div>
              {v.proj.library_version ? (
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium uppercase tracking-wide text-text-muted">Bibliotheekversie dossier</dt>
                  <dd className="mt-0.5 font-mono text-sm font-medium text-text">{v.proj.library_version}</dd>
                  <p className="mt-1 text-xs text-text-muted">
                    Vastgezet bij aanmaak; wijzigt niet automatisch bij catalogusupdates.
                  </p>
                </div>
              ) : null}
            </dl>
          </div>
        ) : (
          <>
            {v.typesLoadError && (
              <div
                className="mb-4 flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between"
                role="status"
              >
                <span>{v.typesLoadError}</span>
                <button
                  type="button"
                  onClick={() => void v.loadReferenceTypes()}
                  className="shrink-0 rounded-md bg-amber-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-900 dark:bg-amber-700 dark:hover:bg-amber-600"
                >
                  Opnieuw proberen
                </button>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 flex items-center gap-1 text-sm font-medium text-text">
                  Projecttype
                  <Tooltip
                    iconOnly
                    label="Uitleg projecttype"
                    content="Bepaalt welke library-posten en indexen passen bij jouw opdracht (bijv. renovatie vs nieuwbouw)."
                  />
                </label>
                <select
                  value={v.projectType}
                  onChange={(e) => v.setProjectType(e.target.value)}
                  disabled={v.typesLoading}
                  className={`${v.inputClass} disabled:opacity-60`}
                >
                  {v.projectTypeOptionsMerged.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 flex items-center gap-1 text-sm font-medium text-text">
                  Gebouwtype
                  <Tooltip
                    iconOnly
                    label="Uitleg gebouwtype"
                    content="Verfijnt het prijsboek: woning, utiliteit enz. Combineert met projecttype voor catalogusfilter."
                  />
                </label>
                <select
                  value={v.buildingType}
                  onChange={(e) => v.setBuildingType(e.target.value)}
                  disabled={v.typesLoading}
                  className={`${v.inputClass} disabled:opacity-60`}
                >
                  {v.buildingTypeOptionsMerged.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-text">Postcode</label>
                <div className="relative">
                  <input
                    type="text"
                    value={v.postalCode}
                    onChange={(e) => v.setPostalCode(e.target.value)}
                    onBlur={() => void v.handleSuggestRegionFromPostal()}
                    placeholder="1234 AB"
                    autoComplete="postal-code"
                    className={`${v.inputClass} w-full font-mono pr-10`}
                  />
                  {v.regionSuggestLoading && (
                    <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-text-muted" />
                  )}
                </div>
                <p className="mt-1 text-xs text-text-muted">
                  Bij een geldige NL-postcode wordt de regio automatisch bepaald. Pas zo nodig handmatig aan onder
                  Regio-ID.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Klanttype</label>
                <select
                  value={
                    CUSTOMER_TYPE_OPTIONS.some((o) => o.id === v.customerType)
                      ? v.customerType
                      : v.customerType && v.customerType !== 'particulier'
                        ? 'anders'
                        : 'particulier'
                  }
                  onChange={(e) => v.setCustomerType(e.target.value)}
                  className={v.inputClass}
                >
                  {CUSTOMER_TYPE_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-text-muted">
                  Tone-of-voice en uitleg in het dossier worden hierop afgestemd (o.a. resultaat en rapportage).
                </p>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-text">Regio-ID (prijsfactor)</label>
                <input
                  type="text"
                  value={v.regionId}
                  onChange={(e) => v.setRegionId(e.target.value)}
                  placeholder="Wordt ingevuld bij geldige postcode, of kies handmatig"
                  className={v.inputClass}
                />
              </div>
              <div className="sm:col-span-2 rounded-xl border border-border/80 bg-surface px-4 py-3 text-sm text-text-muted">
                <span className="font-medium text-text">Prijspeil: </span>
                indexjaar <strong className="text-text">{v.indexForPricing}</strong> (dataset in bibliotheek; nu alleen dit
                jaar beschikbaar). Vast voor consistente doorrekening — niet instelbaar per gebruiker.
              </div>
              {v.proj.library_version ? (
                <div className="sm:col-span-2 rounded-xl border border-border/80 bg-surface px-4 py-3 text-sm">
                  <span className="font-medium text-text">Bibliotheekversie dossier: </span>
                  <span className="font-mono text-text">{v.proj.library_version}</span>
                  <p className="mt-1 text-xs text-text-muted">
                    Vastgezet bij aanmaak; wijzigt niet automatisch bij catalogusupdates.
                  </p>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => void v.handleSaveContext()}
              disabled={v.contextSaving}
              className={`mt-5 ${v.btnPrimary}`}
            >
              {v.contextSaving ? 'Opslaan…' : 'Wijzigingen opslaan'}
            </button>
          </>
        )}
      </ProjectDetailSection>

      <ProjectDetailSection
        id="documenten-bibliotheek"
        icon={FolderOpen}
        title="Documentenbibliotheek"
        description="Alle tekeningen en PDF’s op dit dossier. Je kunt ze openen, hernoemen (potlood) en — onder Tekening analyseren — opnieuw selecteren voor een run zonder opnieuw te uploaden. Zelfde limieten als nieuwe uploads (max. 10 platen per analyse, PDF’s worden per pagina geteld)."
      >
        <ProjectLibraryDocuments
          projectId={v.projectId}
          tenantId={v.tenantIdForApi}
          documents={v.proj.library_documents ?? []}
          btnSecondary={v.btnSecondary}
          onUpdated={async (next) => {
            await api.updateProject(
              v.projectId,
              { tenant_id: v.tenantIdForApi, library_documents: next },
              { revisionSeq: freshRevisionSeq() }
            );
            void v.queryClient.invalidateQueries({ queryKey: ['project', v.projectId] });
          }}
        />
      </ProjectDetailSection>

      {draftProposal?.status === 'draft' && proposalCount > 0 && (
        <ProjectDetailSection
          id="tekening-voorstel-actief"
          icon={Upload}
          title="Tekeningvoorstel op dit dossier"
          description={
            v.hasSystems
              ? 'Er is een nieuw analysevoorstel. Op Posten kies je vóór goedkeuren: toevoegen, vervangen of samenvoegen bij gelijke regels. Je kunt het voorstel ook wissen.'
              : 'Er is al een analyse gekoppeld. Ga naar Posten om te controleren en goed te keuren, of wis het voorstel om opnieuw te beginnen.'
          }
        >
          <div className="flex flex-col gap-3 rounded-lg border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
            {v.hasSystems && (
              <p className="rounded-md border border-amber-400/50 bg-amber-100/80 px-2 py-1.5 text-xs font-medium text-amber-950 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-50">
                Dit dossier heeft al opgeslagen posten — op Posten kies je hoe het voorstel wordt toegepast (niet stil overschrijven).
              </p>
            )}
            {draftProposal.analysis_summary && (
              <p className="text-text-muted">{draftProposal.analysis_summary}</p>
            )}
            <p className="font-medium text-text">
              {proposalCount} voorgestelde {proposalCount === 1 ? 'post' : 'posten'} wacht op goedkeuring.
            </p>
            <div className="flex flex-wrap gap-2">
              <Link
                to={`/projects/${v.projectId}/posten`}
                className={`${v.btnPrimary} inline-flex text-center no-underline`}
              >
                Naar Posten → goedkeuren
              </Link>
              <button type="button" className={v.btnSecondary} onClick={() => void v.handleClearDrawingProposal()}>
                Voorstel wissen
              </button>
            </div>
          </div>
        </ProjectDetailSection>
      )}

      <ProjectDetailSection
        id="tekening-upload"
        icon={Upload}
        title="Tekening analyseren"
        description={
          v.hasSystems ? (
            <>
              Nieuwe upload of platen uit de <strong className="text-text">documentenbibliotheek</strong> hieronder.
              Analyse blijft op het dossier; je importeert posten <strong className="text-text">per selectie</strong> (ook
              in delen). Diepte en nadruk volgen je <strong className="text-text">klanttype</strong>. Geüploade bestanden
              komen óók in de bibliotheek; hernoem ze voor duidelijkere resultaten.
            </>
          ) : (
            <>
              Upload of kies bestaande documenten uit de bibliotheek. Het analyse-resultaat blijft bewaard tot je opnieuw
              draait, wist, of alle voorgestelde posten hebt geïmporteerd. Conceptvoorstellen worden bij gedeeltelijke
              import <strong className="text-text">aangevuld</strong>.
            </>
          )
        }
      >
        <DrawingUpload
          projectId={v.projectId}
          tenantId={v.tenantIdForApi}
          projectType={v.projectType}
          buildingType={v.buildingType}
          postalCode={v.postalCode}
          customerTypeId={v.customerType}
          templateId={v.proj.template_id}
          indexYear={v.indexForPricing}
          persistedAnalysisCache={v.proj.drawing_analysis_cache ?? null}
          libraryDocuments={v.proj.library_documents ?? []}
          existingDrawingProposal={v.proj.drawing_proposal ?? null}
          revisionSeq={v.proj.revision_seq ?? 1}
          getRevisionSeq={freshRevisionSeq}
          onProposalAttached={() => {
            void v.queryClient.invalidateQueries({ queryKey: ['project', v.projectId] });
          }}
          onAfterUploadToLibrary={async (items) => {
            const fresh = v.queryClient.getQueryData<ProjectRecord>(['project', v.projectId]);
            const current = fresh?.library_documents ?? v.proj.library_documents ?? [];
            const next = [
              ...current,
              ...items.map((it) => ({
                doc_id:
                  typeof crypto !== 'undefined' && 'randomUUID' in crypto
                    ? crypto.randomUUID()
                    : `doc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
                url: it.url,
                label: it.label,
                uploaded_at: new Date().toISOString(),
                content_type: it.content_type,
              })),
            ];
            await api.updateProject(
              v.projectId,
              { tenant_id: v.tenantIdForApi, library_documents: next },
              { revisionSeq: freshRevisionSeq() }
            );
            void v.queryClient.invalidateQueries({ queryKey: ['project', v.projectId] });
          }}
        />
      </ProjectDetailSection>
    </>
  );
}
